import { useEffect, useMemo, useState } from 'react';
import { FaPlug, FaSave } from 'react-icons/fa';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './NetteFaturaAccountsSettingsPage.scss';

const accountDefinitions = [
  {
    type: 'Rental',
    title: 'Vin\u00e7 Kiralama Hesab\u0131',
    description: 'Kiralama faturalar\u0131 bu NetteFatura yetkili VKN/TCKN bilgisi ile kesilir.'
  },
  {
    type: 'TechnicalService',
    title: 'Teknik Servis Hesab\u0131',
    description: 'Teknik servis faturalar\u0131 bu NetteFatura yetkili VKN/TCKN bilgisi ile kesilir.'
  }
];

const emptyAccount = {
  accountType: '',
  companyTitle: '',
  companyTaxCode: '',
  taxOffice: '',
  username: '',
  password: '',
  senderAlias: '',
  iban: '',
  isActive: true,
  hasPassword: false,
  lastTestDate: null,
  lastTestSuccess: null
};

const normalizeAccount = (type, account = {}) => ({
  ...emptyAccount,
  ...account,
  accountType: type,
  password: '',
  isActive: account.isActive ?? true,
  hasPassword: Boolean(account.hasPassword)
});

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR');
};

const getStatusLabel = (value) => {
  if (value === true) return 'Ba\u015far\u0131l\u0131';
  if (value === false) return 'Ba\u015far\u0131s\u0131z';
  return 'Test edilmedi';
};

const NetteFaturaAccountsSettingsPage = () => {
  const [accounts, setAccounts] = useState(() =>
    accountDefinitions.reduce((acc, item) => {
      acc[item.type] = normalizeAccount(item.type);
      return acc;
    }, {})
  );
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState('');
  const [testingType, setTestingType] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const accountMap = useMemo(() => accounts, [accounts]);

  const fetchAccounts = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await api.get('/NetteFaturaAccount');
      const list = response?.data?.data || [];

      setAccounts(
        accountDefinitions.reduce((acc, item) => {
          const found = list.find((account) => {
            const value = account.accountType;
            if (value === item.type) return true;
            if (item.type === 'Rental' && value === 1) return true;
            if (item.type === 'TechnicalService' && value === 2) return true;
            return false;
          });
          acc[item.type] = normalizeAccount(item.type, found);
          return acc;
        }, {})
      );
    } catch (error) {
      console.error('NetteFatura hesaplari yuklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleChange = (type, field, value) => {
    setAccounts((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const saveAccount = async (type, options = {}) => {
    const account = accountMap[type];
    setSavingType(type);
    if (!options.silent) {
      setMessage('');
    }
    setErrorMessage('');

    try {
      if (!account.companyTaxCode?.trim()) {
        setErrorMessage('VKN/TCKN alan\u0131 bo\u015f b\u0131rak\u0131lamaz.');
        return false;
      }

      const payload = {
        accountType: type,
        companyTitle: account.companyTitle,
        companyTaxCode: account.companyTaxCode,
        taxOffice: account.taxOffice,
        username: account.username,
        password: account.password?.trim() ? account.password : null,
        senderAlias: account.senderAlias,
        iban: account.iban,
        isActive: account.isActive
      };

      const response = await api.put('/NetteFaturaAccount', payload);
      const saved = response?.data?.data;

      setAccounts((prev) => ({
        ...prev,
        [type]: normalizeAccount(type, saved || prev[type])
      }));

      if (!options.silent) {
        setMessage('NetteFatura hesab\u0131 kaydedildi.');
      }

      return true;
    } catch (error) {
      console.error('NetteFatura hesabi kaydedilemedi:', error);
      setErrorMessage('NetteFatura hesab\u0131 kaydedilemedi. Bilgileri kontrol edin.');
      return false;
    } finally {
      setSavingType('');
    }
  };

  const testAccount = async (type) => {
    setTestingType(type);
    setMessage('');
    setErrorMessage('');

    try {
      const saved = await saveAccount(type, { silent: true });
      if (!saved) {
        return;
      }

      const response = await api.post(`/NetteFaturaAccount/${type}/test`);
      const result = response?.data?.data;

      setAccounts((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          lastTestDate: result?.testedAt || new Date().toISOString(),
          lastTestSuccess: Boolean(result?.isSuccess)
        }
      }));

      if (result?.isSuccess) {
        setMessage(result.message || 'Ba\u011flant\u0131 testi ba\u015far\u0131l\u0131.');
      } else {
        setErrorMessage(result?.message || 'Ba\u011flant\u0131 testi ba\u015far\u0131s\u0131z.');
      }
    } catch (error) {
      console.error('NetteFatura baglanti testi basarisiz:', error);
      setErrorMessage('Ba\u011flant\u0131 testi \u00e7al\u0131\u015ft\u0131r\u0131lamad\u0131.');
    } finally {
      setTestingType('');
    }
  };

  return (
    <div className="nette-settings-page">
      <Navbar />
      <main className="nette-settings-container">
        <header className="nette-settings-header">
          <div>
            <h1>{'NetteFatura Hesaplar\u0131'}</h1>
            <p>{'Fatura kayna\u011f\u0131na g\u00f6re kullan\u0131lacak hesap bilgilerini y\u00f6netin.'}</p>
          </div>
        </header>

        {message && <div className="nette-alert success">{message}</div>}
        {errorMessage && <div className="nette-alert error">{errorMessage}</div>}

        {loading ? (
          <section className="nette-card loading-card">{'Hesaplar y\u00fckleniyor...'}</section>
        ) : (
          <div className="nette-account-grid">
            {accountDefinitions.map((definition) => {
              const account = accountMap[definition.type];

              return (
                <section className="nette-card" key={definition.type}>
                  <div className="nette-card-header">
                    <div>
                      <h2>{definition.title}</h2>
                      <p>{definition.description}</p>
                    </div>
                    <label className="nette-switch">
                      <input
                        type="checkbox"
                        checked={account.isActive}
                        onChange={(event) => handleChange(definition.type, 'isActive', event.target.checked)}
                      />
                      <span>Aktif</span>
                    </label>
                  </div>

                  <div className="nette-form-grid">
                    <label>
                      NetteFatura&apos;da Yetkili VKN/TCKN
                      <input
                        value={account.companyTaxCode}
                        maxLength="11"
                        placeholder="10 veya 11 haneli VKN/TCKN"
                        onChange={(event) => handleChange(definition.type, 'companyTaxCode', event.target.value.replace(/\D/g, ''))}
                      />
                    </label>
                  </div>

                  <div className="nette-test-row">
                    <span className={`test-state ${account.lastTestSuccess === true ? 'success' : account.lastTestSuccess === false ? 'error' : ''}`}>
                      {getStatusLabel(account.lastTestSuccess)}
                    </span>
                    <span>Son test: {formatDate(account.lastTestDate)}</span>
                  </div>

                  <div className="nette-actions">
                    <button type="button" className="secondary-btn" onClick={() => testAccount(definition.type)} disabled={testingType === definition.type || savingType === definition.type}>
                      <FaPlug /> {testingType === definition.type ? 'Test ediliyor...' : 'Ba\u011flant\u0131y\u0131 Test Et'}
                    </button>
                    <button type="button" className="primary-btn" onClick={() => saveAccount(definition.type)} disabled={savingType === definition.type}>
                      <FaSave /> {savingType === definition.type ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default NetteFaturaAccountsSettingsPage;
